import { TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { SaccoMetrics } from "@/hooks/sacco/useSaccoMetrics.ts";

interface NetPositionCardProps {
  metrics: SaccoMetrics;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function NetPositionCard({
  metrics,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: NetPositionCardProps) {
  const { netPosition, totalDeposits, totalWithdrawals, totalShareCapital, outstandingLoanBalance } = metrics;
  const isPositive = netPosition >= 0;

  function convertAmount(raw: number): number {
    if (ratesError || ratesLoading || !convert || displayCurrency === nativeCurrency) return raw;
    try {
      return convert(raw, nativeCurrency, displayCurrency);
    } catch {
      return raw;
    }
  }

  const effectiveCurrency = ratesError ? nativeCurrency : displayCurrency;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Net Position</CardTitle>
        {isPositive ? (
          <TrendingUp className="h-4 w-4 text-green-600" />
        ) : (
          <TrendingDown className="h-4 w-4 text-red-600" />
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className={`text-2xl font-bold ${isPositive ? "text-green-600" : "text-red-600"}`}>
            {ratesLoading ? (
              <Skeleton className="h-8 w-32 inline-block" />
            ) : (
              formatCurrency(convertAmount(netPosition), effectiveCurrency)
            )}
          </p>
          <p className="text-xs text-muted-foreground">Deposits + Shares − Loans</p>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Deposits</span>
            <span className="font-medium">
              {ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmount(totalDeposits), effectiveCurrency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Withdrawals</span>
            <span className="font-medium text-red-600">
              −{ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmount(totalWithdrawals), effectiveCurrency)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Share Capital</span>
            <span className="font-medium">
              {ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmount(totalShareCapital), effectiveCurrency)}
            </span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="text-muted-foreground">Outstanding Loans</span>
            <span className="font-medium text-red-600">
              −{ratesLoading ? <Skeleton className="h-4 w-20 inline-block" /> : formatCurrency(convertAmount(outstandingLoanBalance), effectiveCurrency)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
